import { DataService, ContactService, BookingService, AnalyticsService, LabelService, TaskService, ContactHistoryService, AttachmentService, StaffService, ResourcesService } from './DataService';
import { Contact, ContactFormData, Label, ContactAttachment, ContactSubscription, ContactListItem } from '../types/contacts';
import {ContactHistoryStats} from '../types/analytics';
import { ServiceType } from '../types/services';
import { Booking, BookingFormData, BookingItem } from '../types/bookings';
import { DataStorageService } from './DataStorageService';
import { v4 as uuidv4 } from 'uuid';
import { getStaticBookings, generateContacts, findBooking, initializeContacts, getContactSubscriptions } from '../testData';
import { TaskItem } from '../types/tasks';
import { findTestUser, testUsers } from '../data/testUsers';
import { AuthService } from './AuthService';
import { BusinessStats, Activity } from '../types/analytics';
import { UserInfo, LoginResult } from '../types/auth'; // Add this import if it's not already there
import { 
  getContactTasks, 
  addContactTask, 
  completeContactTask,
  updateContactTask,
  deleteContactTask
} from '../testData';

class MockAuthServiceImpl implements AuthService {
    async login(idToken: string): Promise<LoginResult>;
    async login(email: string, password: string): Promise<LoginResult>;
    async login(arg1: string, arg2?: string): Promise<LoginResult> {
        if (arg2 !== undefined) {
            // email/password login
            console.log('MockAuthServiceImpl.login called with:', arg1);
            const user = findTestUser(arg1, arg2);
            if (user) {
                return {
                    success: true,
                    userID: user.id,
                    user: {
                        id: user.id,
                        email: user.email,
                        name: user.name,
                        role: user.role,
                        phone: user.phone,
                        address: user.address,
                        position: user.position,
                        jobTitle: user.jobTitle
                    }
                };
            }
            return {
                success: false,
                userID: '',
                error: 'Invalid username or password'
            };
        } else {
            // idToken login (mock: always succeed)
            return {
                success: true,
                userID: 'mock-user-id',
                user: {
                    id: 'mock-user-id',
                    email: 'mock-user@example.com',
                    name: 'Mock User',
                    role: 'client',
                    phone: '123-456-7890',
                    address: '123 Mock St',
                    position: 'Software Engineer',
                    jobTitle: 'Senior Developer'
                }
            };
        }
    }

    async verifyLogin(): Promise<{ isLoggedIn: boolean; user?: UserInfo | undefined }> {
        // Create a mock user for testing purposes
        return Promise.resolve({
            isLoggedIn: true,
            user: {
                id: 'mock-user-id',
                email: 'mock-user@example.com',
                name: 'Mock User',
                role: 'client',
                phone: '123-456-7890',
                address: '123 Mock St',
                position: 'Software Engineer',
                jobTitle: 'Senior Developer'
            }
        });
    }

    async logout(): Promise<{ success: boolean; error?: string }> {
        console.log('MockAuthServiceImpl.logout called');
        return Promise.resolve({ success: true });
    }

    async isLoggedIn(): Promise<boolean> {
        return Promise.resolve(true);
    }

    async registerBackend(email: string, _password: string): Promise<LoginResult> {
        console.log('MockAuthService: registerBackend attempt with', email);

        const existingUser = testUsers.find(user => user.email.toLowerCase() === email.toLowerCase());

        if (existingUser) {
            return {
                success: false,
                userID: '',
                error: 'Email already in use'
            };
        }

        return {
            success: true,
            userID: 'new-mock-user-id',
        };
    }

    async resetPassword(email: string): Promise<LoginResult> {
        console.log('MockAuthService: password reset attempt for', email);

        const existingUser = testUsers.find(user => user.email.toLowerCase() === email.toLowerCase());

        if (!existingUser) {
            return {
                success: false,
                userID: '',
                error: 'Account not found'
            };
        }

        return {
            success: true,
            userID: existingUser.id,
        };
    }

    async handleOAuthCallback(code: string, state: string): Promise<LoginResult> {
        console.log('Mock handleOAuthCallback called with:', { code, state });

        return {
            success: true,
            userID: 'mock-user-id',
            user: {
                id: 'mock-user-id',
                email: 'mock-user@example.com',
                name: 'Mock User',
                role: 'client',
                phone: '123-456-7890',
                address: '123 Mock St',
                position: 'Software Engineer',
                jobTitle: 'Senior Developer'
            }
        };
    }

    async updateUserPreferences(preferences: { dashboardShortcuts?: string[] }): Promise<{ success: boolean; error?: string }> {
        console.log('Mock updateUserPreferences called with:', preferences);
        // Mock implementation - just return success
        return { success: true };
    }
}

export class MockDataService implements DataService {
    private contactsData: Contact[] = [];
    private bookingsData: Booking[] = [];
    private labelsData: Label[] = [
        { id: 'label-1', name: 'Contact', color: '#4CAF50' },
        { id: 'label-2', name: 'VIP', color: '#FFC107' },
        { id: 'label-3', name: 'New', color: '#2196F3' },
        { id: 'label-4', name: 'Inactive', color: '#9E9E9E' },
        { id: 'label-5', name: 'Prospect', color: '#673AB7' }
    ];
    // Update the type for cachedServiceTypes
    private cachedServiceTypes: { type: ServiceType; imageUrl: string; serviceId: string }[] | null = null;

    public auth: AuthService;

    // Implement missing DataService interface methods
    async getGlobalFieldOrder(): Promise<string[]> {
        // Return a mock order of global fields
        return Promise.resolve(['firstName', 'lastName', 'email', 'phone', 'address']);
    }

    async saveGlobalFieldOrder(order: string[]): Promise<boolean> {
        // Simulate saving the global field order (no-op for mock)
        console.log('MockDataService: Saved global field order:', order);
        return Promise.resolve(true);
    }

    public contacts!: ContactService;
    public bookings!: BookingService;
    public labels!: LabelService;
    public tasks!: TaskService;
    public contactHistory!: ContactHistoryService;
    public analytics!: AnalyticsService;
    public attachments: AttachmentService;
    public staff!: StaffService;
    public resources!: ResourcesService;

    constructor() {
        this.auth = new MockAuthServiceImpl();

        this.contactsData = generateContacts(20);

        this.contactsData = this.contactsData.map(contact => ({
            ...contact,
            labels: [this.labelsData[Math.floor(Math.random() * this.labelsData.length)].id],
            attachments: []
        }));

        DataStorageService.storeData('mock-labels', this.labelsData);

        this.initializeMockBookings();

        this.initializeServiceInterfaces();

        this.attachments = {
            getAttachments: async (contactId: string) => this.getAttachments(contactId),
            addAttachments: async (contactId: string, file: File) => {
                const newAttachment: ContactAttachment = {
                    id: uuidv4(),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    uploadDate: new Date().toISOString(),
                    url: URL.createObjectURL(file),
                    downloadUrl: URL.createObjectURL(file),
                    date: new Date().toISOString(),
                    contactId: contactId
                };
                await this.addAttachments(contactId, [newAttachment]);
                return newAttachment;
            },
            deleteAttachment: async (contactId: string, attachmentId: string) => this.deleteAttachment(contactId, attachmentId),
            openAttachment: async (attachment: ContactAttachment) => {
                // In a real implementation, this might open a modal or new tab.
                // Here, just simulate opening by returning the URL.
                window.open(attachment.url, '_blank');
                return Promise.resolve();
            }
        };
    }

    private initializeMockBookings() {
        try {
            const today = new Date();
            const bookingDays = getStaticBookings(today);

            this.bookingsData = bookingDays.flatMap((day: { date: Date; items: BookingItem[] }) =>
                day.items.map((appt: BookingItem) => this.mapBookingToBooking(appt))
            );
        } catch (e) {
            console.error('Error initializing bookings:', e);
            this.createMockBookings();
        }
    }

    private mapBookingToBooking(appt: BookingItem): Booking {
        // Convert string times to Date objects
        const bookingDate = new Date(appt.date || new Date());

        let startTime: Date;
        let endTime: Date;

        if (appt.startTime instanceof Date) {
            startTime = appt.startTime;
        } else {
            const [startHour, startMinute] = appt.startTime.split(':').map(Number);
            startTime = new Date(bookingDate);
            startTime.setHours(startHour, startMinute, 0, 0);
        }

        if (appt.endTime instanceof Date) {
            endTime = appt.endTime;
        } else {
            const [endHour, endMinute] = appt.endTime.split(':').map(Number);
            endTime = new Date(bookingDate);
            endTime.setHours(endHour, endMinute, 0, 0);
        }

        return {
            ...appt,  // Copy all fields from appt
            date: bookingDate.toISOString(), // Convert Date to ISO string
            startTime,
            endTime,
            contactId: appt.contactId || appt.contact?.id || '1',
            contact: appt.contact,
            location: appt.location || appt.contact?.address || '',
            serviceType: (appt.serviceType as ServiceType) || '',
            notes: appt.notes || '',
            status: (appt.status as 'pending' | 'confirmed' | 'completed' | 'cancelled') || 'confirmed',
            createdAt: appt.createdAt || new Date(),
            updatedAt: appt.updatedAt || new Date()
        };
    }

    private createMockBookings() {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        this.bookingsData = [
            {
                id: '1',
                title: 'Regular Cleaning - John Doe',
                date: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(),
                startTime: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 0),
                endTime: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 0),
                contactId: '1',
                contact: {
                    id: '1',
                    name: 'John Doe',
                    email: 'john.doe@example.com',
                    phone: '555-123-4567',
                    address: '123 Main St, Anytown, CA'
                },
                status: 'confirmed',
                notes: 'Bring extra supplies',
                location: '123 Main St, Anytown, CA',
                serviceType: 'Regular Clean' as ServiceType,
                createdAt: new Date(),
                updatedAt: new Date()
            },
            {
                id: '2',
                title: 'Deep Clean - Jane Smith',
                date: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate()).toISOString(),
                startTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 13, 0),
                endTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 16, 0),
                contactId: '2',
                contact: {
                    id: '2',
                    name: 'Jane Smith',
                    email: 'jane.smith@example.com',
                    phone: '555-987-6543',
                    address: '456 Oak Ave, Somewhere, NY'
                },
                status: 'pending',
                notes: 'Customer requested special attention to kitchen',
                location: '456 Oak Ave, Somewhere, NY',
                serviceType: 'Deep Clean' as ServiceType,
                createdAt: new Date(),
                updatedAt: new Date()
            }
        ];
    }

    private initializeServiceInterfaces() {
        this.contacts = {
            getAllContacts: async () => {
                // Convert ContactItem[] to ContactList[]
                const contacts = initializeContacts();
                return contacts.map(contact => ({
                    ...contact,
                    _id: contact.id,
                    labelIds: contact.labels || [],
                    contactsCount: 0,
                    clientType: 'individual',
                    lastService: new Date().toISOString(),
                    totalSpent: 0
                })) as unknown as ContactListItem[];
            },
            getContactById: async (id) => this.getContactById(id),
            subscribeToContact: (id, onChange) => {
                // Mock: fire once with current data, no live updates.
                void this.getContactById(id).then(c => { if (c) onChange(c); });
                return () => { /* no-op */ };
            },
            createContact: async (data) => this.createContact(data),
            updateContact: async (contact) => this.updateContact(contact),
            updateDisplayNameSource: async (_id: string, _value: 'company' | 'name' | '') => {
                // Mock implementation - no-op
            },
            uploadContactAvatar: async (_contactId: string, _base64Data: string, _mimeType?: string) => {
                // Mock implementation - return dummy file URL
                return Promise.resolve({ fileUrl: 'mock-avatar-url.jpg' });
            },
            deleteContact: async (id) => this.deleteContact(id),
            searchContacts: async (query: string): Promise<Contact[]> => {
                const contacts = initializeContacts();
                
                // Filter contacts based on the search query
                return contacts.filter((contact) => {
                    const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
                    const email = (typeof contact.emails?.[0] === 'string'
                        ? contact.emails?.[0]
                        : (contact.emails?.[0]?.email ?? '')
                    ).toLowerCase();
                    const phone = typeof contact.phones?.[0] === 'string'
                        ? (contact.phones[0] as string).toLowerCase()
                        : (contact.phones?.[0]?.phone ?? '').toLowerCase();
                    const searchTermLower = query.toLowerCase();
                    
                    return fullName.includes(searchTermLower) || 
                           email.includes(searchTermLower) || 
                           phone.includes(searchTermLower);
                });
            },
            importContacts: async () => {
                console.log('Mock import contacts');
                return initializeContacts().slice(0, 5); // Return 5 mock contacts as if they were imported
            },
            // Stub implementations for missing ContactService methods
            deleteExtendedField: async (_fieldKey: string) => { return true; },
            renameExtendedField: async (_fieldKey: string, _newDisplayName: string) => { return true; },
            getAllCustomFields: async () => { return []; },
            getContactDetails: async (_contactId: string) => { return null; },
            listContactNotes: async (_contactId: string) => {
                const now = new Date().toISOString();
                return [
                    { id: 'mock-1', content: 'Contact prefers weekend bookings.', createdAt: now, updatedAt: now, revision: '1' },
                    { id: 'mock-2', content: 'Follow up about additional cleaning services.', createdAt: now, updatedAt: now, revision: '1' },
                ];
            },
            createContactNote: async (_contactId: string, content: string) => {
                const now = new Date().toISOString();
                return { id: `mock-${Date.now()}`, content, createdAt: now, updatedAt: now, revision: '1' };
            },
            updateContactNote: async (_contactId: string, noteId: string, content: string, _revision: string) => {
                const now = new Date().toISOString();
                return { id: noteId, content, createdAt: now, updatedAt: now, revision: '2' };
            },
            deleteContactNote: async (_contactId: string, _noteId: string) => { return true; },

            // Added missing ContactService interface methods as stubs
            getBulkSubscriptionStatuses: async (emails: string[], phones: string[]) => {
                // Return mock subscription statuses for emails and phones
                const emailSubscriptions = new Map<string, boolean>();
                const smsSubscriptions = new Map<string, boolean>();
                emails.forEach(email => emailSubscriptions.set(email, true));
                phones.forEach(phone => smsSubscriptions.set(phone, true));
                return { emailSubscriptions, smsSubscriptions };
            },
            getEmailSubscriptionStatus: async (_email: string) => { return true; },
            // Added missing ContactService interface methods to fix compile error
            getSMSSubscriptionStatus: async (_phone: string) => { return true; },
            getAllSubscriptionData: async () => {
                // Return mock data for email and sms subscribers
                return {
                    emailSubscribers: ['mock1@example.com', 'mock2@example.com'],
                    smsSubscribers: ['123-456-7890', '987-654-3210']
                };
            },
            // Added missing ContactService interface method
            getFilteredContacts: ({ contacts, searchText, activeFilter, selectedLabelId }) => {
                const lowerSearch = searchText.trim().toLowerCase();
                let filtered = contacts;
                if (activeFilter && activeFilter !== 'all') {
                    // Use clientType from contactfilter if present
                    filtered = filtered.filter(c => {
                        const clientTypeArr = c.contactfilter?.[0]?.clientType;
                        if (Array.isArray(clientTypeArr)) {
                            return clientTypeArr.map(t => t.toLowerCase()).includes(activeFilter.toLowerCase());
                        }
                        if (typeof clientTypeArr === 'string') {
                            return clientTypeArr.toLowerCase() === activeFilter.toLowerCase();
                        }
                        return false;
                    });
                }
                if (selectedLabelId) {
                    filtered = filtered.filter(c => (c.labels || []).includes(selectedLabelId));
                }
                if (lowerSearch) {
                    filtered = filtered.filter(contact => {
                        const name = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
                        const displayName = (contact.displayName || '').toLowerCase();
                        const email = (contact.primaryEmail || '').toLowerCase();
                        const phone = (contact.primaryPhone || '').toLowerCase();
                        return (
                            name.includes(lowerSearch) ||
                            displayName.includes(lowerSearch) ||
                            email.includes(lowerSearch) ||
                            phone.includes(lowerSearch)
                        );
                    });
                }
                return filtered;
            },
        };

        this.bookings = {
            getAllBookings: async () => this.getAllBookings(),
            getBookingById: async (id) => this.getBookingById(id),
            createBooking: async (data) => this.createBooking(data),
            createBookingWithAvailabilityCheck: async (data, options) => this.createBookingWithAvailabilityCheck(data, options),
            updateBooking: async (booking) => this.updateBooking(booking),
            updateBookingWithAvailabilityCheck: async (booking, options) => this.updateBookingWithAvailabilityCheck(booking, options),
            deleteBooking: async (id) => this.deleteBooking(id),
            deleteBookingWithOptions: async (id, options) => this.deleteBookingWithOptions(id, options),
            getBookingsByDateRange: async (start, end) => this.getBookingsByDateRange(start, end),
            getBookingsForDay: async (date) => this.getBookingsForDay(date),
            getUpcomingBookings: async (limit) => this.getUpcomingBookings(limit),
            updateBookingStatus: async (id, status) => { console.log(`Update booking ${id} status: ${status}`); },
            setBookingAttended: async (id) => { console.log(`Mark booking ${id} attended`); },
            getBookingsAttendance: async () => ({}),            cancelBooking: async (id) => { console.log(`Cancel booking ${id}`); },
            // Added missing BookingService interface methods
            getBookingsByContactIdAndDateRange: async (contactId, startDate, endDate) => {
                // Return mock bookings for the contact in the date range
                const allBookings = await this.getAllBookings();
                return allBookings.filter(b => b.contactId === contactId && new Date(b.startTime) >= startDate && new Date(b.startTime) <= endDate);
            },
            getBookingsAndBlockedSlotsByDateRange: async (startDate, endDate, serviceIds) => {
                // Return mock BookingItem[] for the date range and serviceIds
                const allBookings = await this.getAllBookings();
                return allBookings.filter(b => {
                    const inRange = new Date(b.startTime) >= startDate && new Date(b.startTime) <= endDate;
                    const matchesService = !serviceIds.length || serviceIds.includes((b.serviceType as string) || '');
                    return inRange && matchesService;
                }) as import('../types/bookings').BookingItem[];
            },
            getBlockedSlotsByDateRange: async () => {
                return [] as import('../types/bookings').BookingItem[];
            }
        };

        this.labels = {
            getAllLabels: async () => this.getLabels(),
            getContactLabels: async (contactId) => this.getContactLabels(contactId),
            assignLabelsToContact: async (contactId, labelIds) => this.assignLabelsToContact(contactId, labelIds),
            createLabel: async (name: string, color?: string) => ({ id: `label-${Date.now()}`, name, color: color || '#cccccc' }),
            updateLabel: async (id: string, name: string, color?: string) => {
                const labelIndex = this.labelsData.findIndex(l => l.id === id);
                if (labelIndex >= 0) {
                    this.labelsData[labelIndex] = { ...this.labelsData[labelIndex], name, color: color || this.labelsData[labelIndex].color };
                    DataStorageService.storeData('mock-labels', this.labelsData);
                    return this.labelsData[labelIndex];
                }
                return { id, name, color: color || '#cccccc' };
            },
            deleteLabel: async () => true,
            getContactsByLabel: async (labelId) => {
                const contacts = initializeContacts();
                return contacts.filter(contact => contact.labels?.includes(labelId));
            },
            // Added missing methods for LabelService interface
            removeLabelsFromContact: async (contactId: string, labelIds: string[]) => {
                const contacts = initializeContacts();
                const contactIndex = contacts.findIndex(c => c.id === contactId);
                if (contactIndex === -1) return false;
                contacts[contactIndex] = {
                    ...contacts[contactIndex],
                    labels: (contacts[contactIndex].labels || []).filter((id: string) => !labelIds.includes(id))
                };
                DataStorageService.storeData('mock-contacts', contacts);
                return true;
            },
            searchContactsByLabel: async (labelId: string) => {
                // Return the label(s) matching the labelId as expected by the interface
                return this.labelsData.filter(label => label.id === labelId);
            },
            getContactCountsByLabel: async () => {
                const contacts = initializeContacts();
                const counts: { [labelId: string]: number } = {};
                this.labelsData.forEach(label => {
                    counts[label.id] = contacts.filter(contact => contact.labels?.includes(label.id)).length;
                });
                return counts;
            }
        };

        this.tasks = {
            getContactTasks: async (contactId: string) => getContactTasks(contactId),
            addContactTask: async (contactId: string, task: Partial<TaskItem>) => addContactTask(contactId, task),
            completeContactTask: async (contactId: string, taskId: string) => completeContactTask(contactId, taskId),
            updateContactTask: async (contactId: string, taskId: string, updates: Partial<TaskItem>) => updateContactTask(contactId, taskId, updates),
            deleteContactTask: async (contactId: string, taskId: string) => deleteContactTask(contactId, taskId)
        };

        this.contactHistory = {
            getContactHistoryStats: async (contactId, period) => this.getContactHistoryStats(contactId, period),
            getContactSubscriptions: async (contactId) => this.getContactSubscriptions(contactId)
        };

        this.analytics = {
            getRecentActivities: async () => this.getRecentActivities(),
            getBusinessStats: async () => this.getBusinessStats()
        };

        // Mock Staff Service
        this.staff = {
            getAllStaffMembers: async () => [
                { _id: '1', name: 'John Smith', email: 'john@example.com', phone: '555-0101' },
                { _id: '2', name: 'Maria Garcia', email: 'maria@example.com', phone: '555-0102' },
                { _id: '3', name: 'David Lee', email: 'david@example.com', phone: '555-0103' },
                { _id: '4', name: 'Sarah Johnson', email: 'sarah@example.com', phone: '555-0104' },
                { _id: '5', name: 'Michael Brown', email: 'michael@example.com', phone: '555-0105' },
                { _id: '6', name: 'Emma Wilson', email: 'emma@example.com', phone: '555-0106' }
            ],
            getStaffMemberById: async (id: string) => {
                const staff = await this.staff.getAllStaffMembers();
                return staff.find(s => s._id === id) || null;
            },
            queryStaffMembers: async () => ({ items: await this.staff.getAllStaffMembers() }),
            createStaffMember: async (staffData) => ({ _id: uuidv4(), name: 'New Staff', ...staffData }),
            updateStaffMember: async (id: string, staffData) => ({ _id: id, name: 'Updated Staff', ...staffData }),
            deleteStaffMember: async () => true,
            getStaffMemberAvailability: async () => [],
            searchStaffMembers: async (query: string) => {
                const staff = await this.staff.getAllStaffMembers();
                return staff.filter(s => s.name.toLowerCase().includes(query.toLowerCase()));
            }
        };

        // Mock Resources Service  
        this.resources = {
            getAllResources: async () => [
                { _id: '1', name: 'Car 01', type: 'VEHICLE' },
                { _id: '2', name: 'Car 02', type: 'VEHICLE' },
                { _id: '3', name: 'Car 03', type: 'VEHICLE' },
                { _id: '4', name: 'Car 04', type: 'VEHICLE' },
                { _id: '5', name: 'Equipment Set A', type: 'EQUIPMENT' },
                { _id: '6', name: 'Equipment Set B', type: 'EQUIPMENT' }
            ],
            getResourceById: async (id: string) => {
                const resources = await this.resources.getAllResources();
                return resources.find(r => r._id === id) || null;
            },
            queryResources: async () => ({ items: await this.resources.getAllResources() }),
            createResource: async (resourceData) => ({ _id: uuidv4(), name: 'New Resource', type: 'RESOURCE', ...resourceData }),
            updateResource: async (id: string, resourceData) => ({ _id: id, name: 'Updated Resource', type: 'RESOURCE', ...resourceData }),
            deleteResource: async () => true,
            searchResources: async (query: string) => {
                const resources = await this.resources.getAllResources();
                return resources.filter(r => r.name.toLowerCase().includes(query.toLowerCase()));
            },
            getResourceAvailability: async () => [],
            getAllResourceTypes: async () => [
                { _id: 'type1', name: 'R&S Maids Team 1' },
                { _id: 'type2', name: 'R&S Maids Team 2' },
                { _id: 'type3', name: 'Individual Staff' }
            ],
            createResourceType: async (name: string) => ({ _id: uuidv4(), name, revision: '1' }),
            updateResourceType: async () => {},
            deleteResourceType: async () => {},
            getResourcesGroupedByCategory: async () => {
                const resources = await this.resources.getAllResources();
                const grouped: { [category: string]: typeof resources[0][] } = {};
                resources.forEach(resource => {
                    // Simple grouping by type for mock data
                    const category = resource.type || 'Other';
                    if (!grouped[category]) {
                        grouped[category] = [];
                    }
                    grouped[category].push(resource);
                });
                return grouped;
            },
            getBusinessLocations: async () => [
                { _id: 'loc1', name: 'Main Office' },
            ]
        };
    }

    async getAllContacts(): Promise<Contact[]> {
        return initializeContacts();
    }
    async getContactById(id: string): Promise<Contact | null> {
        const storedContacts = DataStorageService.getData<Contact[]>('mock-contacts');
        if (storedContacts) {
            this.contactsData = storedContacts;
        }
        const contacts = initializeContacts();

        const contact = contacts.find(c => c.id === id);
        return contact || null;
    }

    async createContact(contactData: ContactFormData): Promise<Contact> {
        const contacts = initializeContacts();

        // Exclude 'id' from contactData to avoid duplicate 'id' property
        const { id: _id, ...contactDataWithoutId } = contactData;
        const newContact: Contact = {
            id: uuidv4(),
            ...contactDataWithoutId,
            labels: [],
            attachments: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            _id: ''
        };

        contacts.push(newContact);

        this.contactsData.push(newContact);

        DataStorageService.storeData('mock-contacts', contacts);

        return { ...newContact };
    }

    async updateContact(contact: Contact): Promise<Contact> {
        const contacts = initializeContacts();

        const index = contacts.findIndex(c => c.id === contact.id);

        if (index !== -1) {
            const updatedContact = {
                ...contacts[index],
                ...contact,
                updatedAt: new Date().toISOString()
            };

            contacts[index] = updatedContact;

            const instanceIndex = this.contactsData.findIndex(c => c.id === contact.id);
            if (instanceIndex !== -1) {
                this.contactsData[instanceIndex] = updatedContact;
            }

            DataStorageService.storeData('mock-contacts', contacts);

            return { ...updatedContact };
        }

        throw new Error(`Contact with ID ${contact.id} not found`);
    }
    async deleteContact(id: string): Promise<boolean> {
        const initialLength = this.contactsData.length;
        this.contactsData = this.contactsData.filter(c => c.id !== id);

        DataStorageService.storeData('mock-contacts', this.contactsData);

        return this.contactsData.length < initialLength;
    }

    async getAllBookings(): Promise<Booking[]> {
        return [...this.bookingsData];
    }

    async getBookingById(id: string): Promise<Booking | null> {
        const booking = this.bookingsData.find(b => b.id === id);
        return booking || null;
    }
    
    async createBooking(bookingData: BookingFormData): Promise<Booking> {
        // Helper function to safely convert to ISO string
        const toISOString = (value: Date | string | undefined | null): string => {
            if (value instanceof Date) {
                return value.toISOString();
            }
            if (typeof value === 'string') {
                return value;
            }
            return new Date().toISOString();
        };

        const newBooking: Booking = {
            id: `booking-${Date.now()}`,
            contactId: bookingData.contactId,
            contact: bookingData.contact,
            // Fix the date handling to avoid type errors
            date: toISOString(bookingData.date || bookingData.startTime || new Date()),
            startTime: bookingData.startTime,
            endTime: bookingData.endTime || new Date(
                bookingData.startTime instanceof Date ? 
                    bookingData.startTime.getTime() + 2 * 60 * 60 * 1000 : 
                    new Date().getTime() + 2 * 60 * 60 * 1000
            ),
            status: bookingData.status || 'pending',
            notes: bookingData.notes || '',
            createdAt: new Date(),
            updatedAt: new Date(),
            title: bookingData.title || `Booking ${Date.now()}`,
            location: bookingData.location || '',
            serviceType: bookingData.serviceType || 'Recurring Cleaning'
        };

        const bookings = this.getStoredBookings();
        bookings.push(newBooking);
        this.storeBookings(bookings);

        return Promise.resolve(newBooking);
    }

    async createBookingWithAvailabilityCheck(bookingData: BookingFormData, options?: { adminOverride?: boolean; userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<{
        booking?: Booking;
        warning?: string;
        error?: string;
        availabilityStatus: 'available' | 'unavailable' | 'admin_override';
    }> {
        try {
            const booking = await this.createBooking(bookingData);
            const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
            
            return {
                booking,
                availabilityStatus: isAdminUser ? 'admin_override' : 'available'
            };
        } catch (error) {
            return {
                error: `Failed to create booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
                availabilityStatus: 'unavailable'
            };
        }
    }

    async updateBooking(booking: Booking): Promise<Booking> {
        const result = await this.updateBookingWithAvailabilityCheck(booking);
        
        if (result.error) {
            throw new Error(result.error);
        }
        
        if (!result.booking) {
            throw new Error('Failed to update booking');
        }
        
        return result.booking;
    }

    async updateBookingWithAvailabilityCheck(booking: Booking, options?: { adminOverride?: boolean; userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<{
        booking?: Booking;
        warning?: string;
        error?: string;
        availabilityStatus: 'available' | 'unavailable' | 'admin_override';
    }> {
        try {
            const bookings = this.getStoredBookings();
            const index = bookings.findIndex(b => b.id === booking.id);

            if (index >= 0) {
                const updatedBooking = {
                    ...booking,
                    updatedAt: new Date()
                };
                bookings[index] = updatedBooking;
                this.storeBookings(bookings);
                
                const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
                
                return {
                    booking: updatedBooking,
                    availabilityStatus: isAdminUser ? 'admin_override' : 'available'
                };
            } else {
                return {
                    error: `Booking with ID ${booking.id} not found`,
                    availabilityStatus: 'unavailable'
                };
            }
        } catch (error) {
            return {
                error: `Failed to update booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
                availabilityStatus: 'unavailable'
            };
        }
    }

    async deleteBooking(id: string): Promise<boolean> {
        return this.deleteBookingWithOptions(id);
    }

    async deleteBookingWithOptions(id: string, options?: { userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<boolean> {
        try {
            // For delete operations, check if user has permission
            const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
            
            if (!isAdminUser) {
                console.log('📅 Non-admin user attempting to delete booking - additional validation may be needed');
            }
            
            const bookings = this.getStoredBookings();
            const initialLength = bookings.length;
            const filteredBookings = bookings.filter(b => b.id !== id);
            
            if (filteredBookings.length < initialLength) {
                this.storeBookings(filteredBookings);
                console.log(`📅 Booking ${id} deleted by ${options?.userRole || 'unknown'} user`);
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Error deleting booking:', error);
            return false;
        }
    }

    async getBookingsByDateRange(startDate: Date, endDate: Date): Promise<Booking[]> {
        return this.bookingsData.filter(booking =>
            booking.startTime >= startDate && booking.startTime <= endDate
        );
    }

    async getBookingsForDay(date: Date): Promise<Booking[]> {
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        return this.bookingsData.filter(booking =>
            booking.startTime >= startOfDay && booking.startTime <= endOfDay
        );
    }

    async getUpcomingBookings(limit: number = 5): Promise<Booking[]> {
        const now = new Date();
        return this.bookingsData
            .filter(booking => booking.startTime >= now)
            .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
            .slice(0, limit);
    }

    async getbookingById(id: string): Promise<BookingItem | undefined> {
        const storedbooking = DataStorageService.getData<BookingItem>(`booking-${id}`);
        if (storedbooking) {
            return storedbooking;
        }

        return findBooking(id);
    }

    async updatebookingStatus(id: string, isCompleted: boolean): Promise<void> {
        DataStorageService.storeData(`booking-${id}-completed`, isCompleted);

        await new Promise(resolve => setTimeout(resolve, 300));

        console.log(`MockDataService: Updated booking ${id} status to ${isCompleted} in mock database`);
    }

    async cancelbooking(id: string): Promise<void> {
        console.log(`MockDataService: Cancelled booking ${id}`);

        DataStorageService.storeData(`booking-${id}-cancelled`, true);
    }

    async getLabels(): Promise<Label[]> {
        const storedLabels = DataStorageService.getData<Label[]>('mock-labels');
        if (storedLabels && storedLabels.length > 0) {
            this.labelsData = storedLabels;
        }
        return [...this.labelsData];
    }

    async getContactLabels(contactId: string): Promise<Label[]> {
        const contact = await this.getContactById(contactId);
        if (!contact || !contact.labels) {
            return [];
        }

        return this.labelsData.filter(label => contact.labels?.includes(label.id));
    }

    async assignLabelsToContact(contactId: string, labelIds: string[]): Promise<boolean> {
        const contacts = initializeContacts();

        const contactIndex = contacts.findIndex(c => c.id === contactId);
        if (contactIndex === -1) {
            return false;
        }

        contacts[contactIndex] = {
            ...contacts[contactIndex],
            labels: labelIds
        };

        DataStorageService.storeData('mock-contacts', contacts);

        return true;
    }

    async getAttachments(contactId: string): Promise<ContactAttachment[]> {
        const contact = await this.getContactById(contactId);
        return contact?.attachments || [];
    }

    async addAttachments(contactId: string, attachments: ContactAttachment[]): Promise<boolean> {
        const contactIndex = this.contactsData.findIndex(c => c.id === contactId);
        if (contactIndex === -1) {
            return false;
        }

        this.contactsData[contactIndex] = {
            ...this.contactsData[contactIndex],
            attachments: [
                ...(this.contactsData[contactIndex].attachments || []),
                ...attachments
            ]
        };

        DataStorageService.storeData('mock-contacts', this.contactsData);

        return true;
    }

    async deleteAttachment(contactId: string, attachmentId: string): Promise<boolean> {
        const contactIndex = this.contactsData.findIndex(c => c.id === contactId);
        if (contactIndex === -1) {
            return false;
        }

        this.contactsData[contactIndex] = {
            ...this.contactsData[contactIndex],
            attachments: this.contactsData[contactIndex].attachments?.filter((a: ContactAttachment) => a.id !== attachmentId) || []
        };

        DataStorageService.storeData('mock-contacts', this.contactsData);

        return true;
    }

    private getStoredBookings(): Booking[] {
        const storedData = DataStorageService.getData<Booking[]>('mock-bookings');
        return storedData || [];
    }

    private storeBookings(bookings: Booking[]): void {
        DataStorageService.storeData('mock-bookings', bookings);
    }

    async getAllbookings(): Promise<BookingItem[]> {
        const today = new Date();
        const bookingDays = getStaticBookings(today);
        return bookingDays.flatMap(day => day.items);
    }

    async getServiceTypes(): Promise<{ type: ServiceType, imageUrl: string, serviceId: string }[]> {
        if (this.cachedServiceTypes) {
            return this.cachedServiceTypes;
        }

        this.cachedServiceTypes = [
            { type: 'Deep Cleaning', imageUrl: '/assets/images/services/deep-clean.svg', serviceId: 'mock-deep-cleaning' },
            { type: 'Move Out Cleaning', imageUrl: '/assets/images/services/move-out.svg', serviceId: 'mock-move-out-cleaning' },
            { type: 'Window Cleaning', imageUrl: '/assets/images/services/windows.svg', serviceId: 'mock-window-cleaning' },
            { type: 'Free Estimate', imageUrl: '/assets/images/services/estimate.svg', serviceId: 'mock-free-estimate' },
            { type: 'Refrigerator Cleaning', imageUrl: '/assets/images/services/refrigerator.svg', serviceId: 'mock-refrigerator-cleaning' },
            { type: 'Post Construction Cleaning', imageUrl: '/assets/images/services/construction.svg', serviceId: 'mock-post-construction-cleaning' },
            { type: 'Recurring Cleaning', imageUrl: '/assets/images/services/recurring.svg', serviceId: 'mock-recurring-cleaning' },
            { type: 'Oven Cleaning', imageUrl: '/assets/images/services/oven.svg', serviceId: 'mock-oven-cleaning' },
            { type: 'AirBnb Cleaning Service', imageUrl: '/assets/images/services/airbnb.svg', serviceId: 'mock-airbnb-cleaning' },
            { type: 'Commercial Cleaning', imageUrl: '/assets/images/services/commercial.svg', serviceId: 'mock-commercial-cleaning' },
            { type: 'Office Cleaning', imageUrl: '/assets/images/services/office.svg', serviceId: 'mock-office-cleaning' }
        ];

        return this.cachedServiceTypes;
    }

    async getContactSubscriptions(contactId: string): Promise<ContactSubscription[]> {
        console.log('MockDataService: Fetching subscriptions for contact:', contactId);
        try {
            const subscriptions = getContactSubscriptions(contactId);
            console.log('MockDataService: Retrieved subscriptions:', subscriptions);

            // Map subscriptions to ensure they have all required properties
            return subscriptions.map(sub => ({
                id: sub.id,
                name: `Subscription ${sub.id}`,
                status: sub.status.toLowerCase() as 'active' | 'inactive' | 'expired',
                nextBillingDate: typeof sub.nextDate === 'string' ? sub.nextDate :
                    (sub.nextDate || new Date().toISOString().split('T')[0]),
                amount: sub.amount,
                contactId: contactId,
                type: 'standard',
                planName: sub.planName || 'Standard Plan',
                startDate: new Date(),
                frequency: 'monthly'
            }));
        } catch (error) {
            console.error('Error in getContactSubscriptions:', error);
            return [];
        }
    }

    async getContactHistoryStats(contactId: string, period: string): Promise<ContactHistoryStats> {
        try {
            // Generate mock contact history stats
            const contactSeed = contactId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
            const baseServices = (contactSeed % 20) + 1;
            const baseSpent = ((contactSeed % 30) + 1) * 100;

            let periodMultiplier;

            if (period === 'all') {
                const relationshipLength = (contactSeed % 24) + 12;
                periodMultiplier = relationshipLength;
            } else {
                periodMultiplier = parseInt(period, 10) / 30;
            }

            const totalServices = Math.floor(baseServices * periodMultiplier);
            const totalSpent = Math.round((baseSpent * periodMultiplier) * 100) / 100;
            return {
                totalServices,
                totalSpent,
                averageServiceValue: totalServices > 0 ? totalSpent / totalServices : 0,
                totalBookings: totalServices,
                cancelledBookings: Math.floor(totalServices * 0.1),
                completedBookings: Math.floor(totalServices * 0.8)
            };
        } catch {
            console.log('Falling back to generated Contact history stats');
            // Fallback to the existing implementation
            const contactSeed = contactId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
            const baseServices = (contactSeed % 20) + 1;
            const baseSpent = ((contactSeed % 30) + 1) * 100;

            let periodMultiplier;

            if (period === 'all') {
                const relationshipLength = (contactSeed % 24) + 12;
                periodMultiplier = relationshipLength;
            } else {
                periodMultiplier = parseInt(period, 10) / 30;
            }

            const totalServices = Math.floor(baseServices * periodMultiplier);
            const totalSpent = Math.round((baseSpent * periodMultiplier) * 100) / 100;
            return {
                totalServices,
                totalSpent,
                averageServiceValue: totalServices > 0 ? totalSpent / totalServices : 0,
                totalBookings: totalServices,
                cancelledBookings: Math.floor(totalServices * 0.1),
                completedBookings: Math.floor(totalServices * 0.8)
            };
        }
    }

    async getContactTasks(contactId: string): Promise<TaskItem[]> {
        const contactSeed = contactId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
        const taskCount = contactSeed % 5;

        const tasks: TaskItem[] = [];
        const priorities: Array<'low' | 'medium' | 'high'> = ['low', 'medium', 'high'];

        for (let i = 0; i < taskCount; i++) {
            const daysOffset = (contactSeed + i) % 14 - 3;
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + daysOffset);

            const priority = priorities[(contactSeed + i) % 3];
            const status = (contactSeed + i) % 4 === 0 ? 'completed' : 'pending';

            tasks.push({
                id: `task-${contactId}-${i}`,
                title: `Task ${i + 1} for this Contact`,
                dueDate: dueDate.toISOString(),
                priority,
                status: status as 'pending' | 'completed',
                completedAt: status === 'completed' ? new Date().toISOString() : null,
                contactId: contactId
            });
        }

        return tasks;
    }

    async addContactTask(contactId: string, task: TaskItem): Promise<TaskItem> {
        const newTask: TaskItem = {
            ...task,
            id: `task-${contactId}-${Date.now()}`,
            dueDate: task.dueDate || new Date().toISOString(),
            completedAt: task.status === 'completed' ? new Date().toISOString() : null,
            contactId: contactId,
            createdAt: new Date().toISOString()
        };

        console.log(`Adding task for contact ${contactId}:`, newTask);

        return newTask;
    }

    async completeContactTask(contactId: string, taskId: string): Promise<boolean> {
        console.log(`Completing task ${taskId} for contact ${contactId}`);

        return new Promise(resolve => {
            setTimeout(() => {
                console.log('Task completed successfully in the mock service');
                resolve(true);
            }, 300);
        });
    }

    getBusinessStats(): Promise<BusinessStats> {
        return Promise.resolve({
            newClients: 12,
            bookingsCount: 27,
            completionRate: 92,
            revenue: 2400,
            growth: 8,
            customerRetentionRate: 85
        });
    }

    getRecentActivities(): Promise<Activity[]> {
        return Promise.resolve([
            {
                id: '1',
                type: 'new-Contact',
                title: 'New Contact Registered',
                detail: 'Sarah Johnson',
                time: '2 hours ago',
                userInitial: 'S'
            },
            {
                id: '2',
                type: 'booking-confirmed',
                title: 'Booking Confirmed',
                detail: 'Deep Cleaning',
                time: 'Today at 3:00 PM',
                userInitial: 'C'
            },
            {
                id: '3',
                type: 'payment',
                title: 'Payment Received',
                detail: '$120.00 - Regular Cleaning',
                time: 'Yesterday at 5:45 PM',
                userInitial: 'P'
            },
            {
                id: '4',
                type: 'invoice-overdue',
                title: 'Invoice Overdue',
                detail: '$180.00 - Robert Smith',
                time: '5 days overdue',
                userInitial: 'R'
            },
            {
                id: '5',
                type: 'new-booking',
                title: 'New Booking Created',
                detail: 'Jessica Lee - Deep Cleaning',
                time: 'For next Thursday',
                userInitial: 'J'
            },
            {
                id: '6',
                type: 'cancel-booking',
                title: 'Booking Cancelled',
                detail: 'Michael Brown - Regular Cleaning',
                time: 'Was for tomorrow',
                userInitial: 'M'
            },
            {
                id: '7',
                type: 'reschedule-booking',
                title: 'Booking Rescheduled',
                detail: 'David Wilson - Regular Cleaning',
                time: 'Moved from Mon to Wed',
                userInitial: 'D'
            }
        ]);
    }
}


